import AddIcon from "@mui/icons-material/Add";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Box, Typography } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useErrorSnackbar } from "@/hooks/useErrorSnackbar";
import { useJobs } from "@/hooks/useJobs";
import { getWeekIndexOfSpecificDate, type WeeksAndActiveState } from "@/pages/MasterSchedule/v2/useGetMasterScheduleWeeksAndActiveState";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { getWeekStart, shiftDateToWeekIndex } from "@/utils/dateUtils";
import { getEnabledNurseJobs } from "@/utils/jobs";
import { ChangesSource, type DepartmentLocation, type Nurse } from "@/utils/models";
import { ShiftListItem } from "./ShiftListItem";
import { doDateRangesOverlap } from "./shiftDateRange";
import {
	generateNewShift,
	type RecurringShiftMasterScheduleProps,
	type RecurringShiftMasterScheduleStateCreateMode,
	type RecurringShiftMasterScheduleStateEditMode,
	recurringShiftMasterScheduleShiftsListTypographySx,
	type SingleRecurringShift,
	SingleRecurringShiftStatus,
	WeekdaysToIndexMap,
	type WeeklyShifts,
} from "./utils";

type Props = {
	title: string;
	isOtherRecurrenceList: boolean;
	shifts: WeeklyShifts;
	shiftsThatRepeatEachWeek: WeeklyShifts;
	onShiftsChange: (_: {
		newShifts: WeeklyShifts;
		removedShiftToAppendToState?: SingleRecurringShift;
		isEveryWeekList: boolean;
		restoredShiftFromRemovedList?: SingleRecurringShift;
	}) => void;
	recurringGeneralState: RecurringShiftMasterScheduleStateCreateMode | RecurringShiftMasterScheduleStateEditMode;
	clickedDate: DateTime;
	weekAndActiveState: WeeksAndActiveState;
	clickedLocation?: DepartmentLocation;
	onHasConflicts?: (hasConflicts: boolean) => void;
	credentialedLocationIds?: string[];
	allCredentialedLocationIds?: string[];
	source: ChangesSource;
	weekStartDay: number;
	/** The staff member picked in the panel, from its roster. */
	selectedNurse: Nurse | undefined;
	suggestStaffShiftDefaults: RecurringShiftMasterScheduleProps["suggestStaffShiftDefaults"];
};

const DAYS_ORDER = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const getNextDay = (day: string): string => {
	const index = DAYS_ORDER.indexOf(day.toLowerCase());
	return DAYS_ORDER[(index + 1) % 7];
};

const parseTime = (time: string): number => {
	const [hours, minutes] = time.split(":").map(Number);
	return hours * 60 + minutes;
};

const isOvernightShift = (startTime: string, endTime: string): boolean => {
	return parseTime(endTime) <= parseTime(startTime);
};

// Allow up to 1 hour (60 minutes) of overlap before considering it a conflict
const ALLOWED_OVERLAP_MINUTES = 60;

/**
 * Check if two time ranges overlap by more than the allowed threshold.
 * Handles overnight shifts (where end time is before start time).
 */
const doTimesOverlap = (start1: string, end1: string, start2: string, end2: string): boolean => {
	const s1 = parseTime(start1);
	let e1 = parseTime(end1);
	const s2 = parseTime(start2);
	let e2 = parseTime(end2);

	// Handle overnight shifts by adding 24 hours to end time if it's before start
	if (e1 <= s1) e1 += 24 * 60;
	if (e2 <= s2) e2 += 24 * 60;

	// Calculate overlap: max(0, min(e1, e2) - max(s1, s2))
	const overlapStart = Math.max(s1, s2);
	const overlapEnd = Math.min(e1, e2);
	const overlapMinutes = Math.max(0, overlapEnd - overlapStart);

	// Only consider it a conflict if overlap exceeds the allowed threshold
	return overlapMinutes > ALLOWED_OVERLAP_MINUTES;
};

/**
 * Check if the morning portion of an overnight shift (00:00 to endTime)
 * overlaps with another shift's time range on the next day by more than allowed.
 */
const doesOvernightMorningOverlap = (overnightEndTime: string, otherStart: string): boolean => {
	const morningEnd = parseTime(overnightEndTime);
	const s2 = parseTime(otherStart);
	// Morning portion is [00:00, morningEnd], other shift starts at s2
	// Overlap is from s2 to morningEnd (if s2 < morningEnd)
	const overlapMinutes = Math.max(0, morningEnd - s2);
	return overlapMinutes > ALLOWED_OVERLAP_MINUTES;
};

/**
 * Check if two shifts conflict:
 * 1. Share at least one day AND have overlapping times, OR
 * 2. An overnight shift's next-day portion overlaps with another shift on that day
 */
const doShiftsConflict = (shift1: SingleRecurringShift, shift2: SingleRecurringShift): boolean => {
	if (shift1.recurrenceDetails.recurringType !== "Weekly" || shift2.recurrenceDetails.recurringType !== "Weekly") return false;
	if (!doDateRangesOverlap(shift1.startDate, shift1.endDate, shift2.startDate, shift2.endDate)) return false;
	const days1 = shift1.recurrenceDetails.weekdays.map((d) => d.toLowerCase());
	const days2 = shift2.recurrenceDetails.weekdays.map((d) => d.toLowerCase());

	const isOvernight1 = isOvernightShift(shift1.startTime, shift1.endTime);
	const isOvernight2 = isOvernightShift(shift2.startTime, shift2.endTime);

	// Check direct day overlap with time overlap
	const hasCommonDay = days1.some((day) => days2.includes(day));
	if (hasCommonDay && doTimesOverlap(shift1.startTime, shift1.endTime, shift2.startTime, shift2.endTime)) {
		return true;
	}

	// Check if shift1 is overnight and its next-day portion overlaps with shift2
	if (isOvernight1) {
		for (const day1 of days1) {
			const nextDay = getNextDay(day1);
			if (days2.includes(nextDay)) {
				if (doesOvernightMorningOverlap(shift1.endTime, shift2.startTime)) {
					return true;
				}
			}
		}
	}

	// Check if shift2 is overnight and its next-day portion overlaps with shift1
	if (isOvernight2) {
		for (const day2 of days2) {
			const nextDay = getNextDay(day2);
			if (days1.includes(nextDay)) {
				if (doesOvernightMorningOverlap(shift2.endTime, shift1.startTime)) {
					return true;
				}
			}
		}
	}

	return false;
};

export const WeeklyShiftsList: FC<Props> = ({
	onShiftsChange,
	shifts,
	isOtherRecurrenceList,
	title,
	recurringGeneralState,
	clickedDate,
	weekAndActiveState,
	clickedLocation,
	shiftsThatRepeatEachWeek,
	onHasConflicts,
	credentialedLocationIds,
	allCredentialedLocationIds,
	source,
	weekStartDay,
	selectedNurse,
	suggestStaffShiftDefaults,
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const { timezone, isMedicalDepartment } = useDepartmentFlags();
	const showErrorSnackbar = useErrorSnackbar();
	const { t } = useTranslation();
	const enabledJobs = useJobs();

	const selectedNurseJobs = useMemo(() => (selectedNurse ? getEnabledNurseJobs(selectedNurse, enabledJobs) : []), [selectedNurse, enabledJobs]);
	const isNurseScheduledForDeactivation =
		!!selectedNurse?.scheduledDeactivationDate && selectedNurse.scheduledDeactivationDate <= weekAndActiveState.firstDate.startOf("day");

	const handleShiftChange = useCallback(
		(updatedShift: SingleRecurringShift, removed: boolean, restoredFromRemoved: boolean) => {
			const relevantShiftsList = updatedShift.isWeeklyShiftThatRepeatsEachWeek ? shiftsThatRepeatEachWeek : shifts;
			if (removed || restoredFromRemoved) {
				const newShifts = relevantShiftsList.filter((shifts) => shifts.id !== updatedShift.id);
				if (updatedShift.status !== SingleRecurringShiftStatus.NEW) {
					const preservedStatus = restoredFromRemoved ? updatedShift.status : SingleRecurringShiftStatus.EXISTING;
					newShifts.push({ ...updatedShift, status: preservedStatus });
				}
				onShiftsChange({
					isEveryWeekList: updatedShift.isWeeklyShiftThatRepeatsEachWeek ?? false,
					newShifts: newShifts,
					...(removed
						? {
								removedShiftToAppendToState: updatedShift,
							}
						: restoredFromRemoved
							? {
									restoredShiftFromRemovedList: updatedShift,
								}
							: {}),
				});
				return;
			}

			const newShifts = [...relevantShiftsList];
			const index = newShifts.findIndex((shift) => shift.id === updatedShift.id);
			if (index >= 0) {
				newShifts[index] = updatedShift;
				onShiftsChange({ isEveryWeekList: updatedShift.isWeeklyShiftThatRepeatsEachWeek ?? false, newShifts });
			} else {
				showErrorSnackbar("Could not find the shift to update", {
					updatedShift,
					shifts,
					relevantShiftsList,
				});
			}
		},
		[onShiftsChange, shifts, shiftsThatRepeatEachWeek, showErrorSnackbar],
	);

	const handleAddShift = useCallback(() => {
		// Calculate staff suggestion for new shifts if a nurse is selected
		const staffShiftDefaultsSuggestion = recurringGeneralState.selectedNurseId
			? suggestStaffShiftDefaults?.(recurringGeneralState.selectedNurseId).at(0)
			: undefined;
		const profileClickedWeekIndex = getWeekIndexOfSpecificDate({
			dateToCheck: clickedDate,
			chosenDepartmentFirstSunday: chosenDepartment.first_sunday,
			masterScheduleWeeks: chosenDepartment.master_schedule_weeks,
			timezone,
			weekStartDay,
		});
		const profileShiftedDate = shiftDateToWeekIndex({
			date: clickedDate,
			fromWeekIndex: profileClickedWeekIndex,
			toWeekIndex: weekAndActiveState.weekIndex,
			masterScheduleWeeks: chosenDepartment.master_schedule_weeks,
		});
		const newShift = generateNewShift({
			startDate: isOtherRecurrenceList
				? clickedDate
				: source === ChangesSource.Profile
					? getWeekStart(profileShiftedDate, weekStartDay)
					: weekAndActiveState.firstDate,
			weeksInterval: chosenDepartment.master_schedule_weeks + (isOtherRecurrenceList ? 1 : 0),
			timezone,
			clickedLocation,
			staffShiftDefaultsSuggestion,
			isMedicalDepartment,
			jobId: selectedNurseJobs.length === 1 ? selectedNurseJobs[0]?.id : undefined,
			consecutive: isOtherRecurrenceList,
		});
		onShiftsChange({
			newShifts: [...shifts, newShift],
			isEveryWeekList: false,
		});
	}, [
		onShiftsChange,
		shifts,
		chosenDepartment.master_schedule_weeks,
		isOtherRecurrenceList,
		weekAndActiveState.firstDate,
		timezone,
		clickedLocation,
		recurringGeneralState.selectedNurseId,
		suggestStaffShiftDefaults,
		isMedicalDepartment,
		source,
		clickedDate,
		chosenDepartment.first_sunday,
		weekStartDay,
		weekAndActiveState.weekIndex,
		selectedNurseJobs,
	]);

	const allShifts = useMemo(
		() =>
			[...shifts, ...shiftsThatRepeatEachWeek]
				.slice()
				.sort((a, b) => {
					// Primary sort by startDate
					const startDateComparison = a.startDate.startOf("day").toMillis() - b.startDate.startOf("day").toMillis();
					if (startDateComparison !== 0) return startDateComparison;
					// Secondary sort by first day of week
					const daysOfA =
						a.recurrenceDetails.recurringType === "Weekly"
							? a.recurrenceDetails.weekdays
									.map((day) => WeekdaysToIndexMap[day])
									.filter((dayIndex) => dayIndex !== undefined)
									.sort((d1, d2) => d1 - d2)
							: [];
					const daysOfB =
						b.recurrenceDetails.recurringType === "Weekly"
							? b.recurrenceDetails.weekdays
									.map((day) => WeekdaysToIndexMap[day])
									.filter((dayIndex) => dayIndex !== undefined)
									.sort((d1, d2) => d1 - d2)
							: [];
					if (daysOfA.length === 0) return -1;
					if (daysOfB.length === 0) return 1;
					const dayComparison = daysOfA[0] - daysOfB[0];
					if (dayComparison !== 0) return dayComparison;
					// Tertiary sort by start time (hours - earliest first)
					return parseTime(a.startTime) - parseTime(b.startTime);
				})
				.map((shift) => ({
					...shift,
					startTime: shift.startTime.substring(0, 5),
					endTime: shift.endTime.substring(0, 5),
				})),
		[shifts, shiftsThatRepeatEachWeek],
	);

	// Detect conflicting shifts (same day + overlapping times)
	const conflictingShiftIds = useMemo(() => {
		const conflicts = new Set<string | number>();
		if (!isMedicalDepartment) {
			const activeShifts = allShifts.filter(
				(s) => s.status !== SingleRecurringShiftStatus.TO_DELETE && !recurringGeneralState.existingShiftsToRemove.find((r) => r.id === s.id),
			);

			for (let i = 0; i < activeShifts.length; i++) {
				for (let j = i + 1; j < activeShifts.length; j++) {
					// On the "other recurring shifts" tab, only mark conflicts when shifts share the same start date and interval
					if (isOtherRecurrenceList) {
						// Skip conflict check for non-Weekly shifts (ConsecutiveDays don't have weekday-based conflicts)
						if (activeShifts[i].recurrenceDetails.recurringType !== "Weekly" || activeShifts[j].recurrenceDetails.recurringType !== "Weekly") {
							continue;
						}
						const interval1 = activeShifts[i].recurrenceDetails.interval;
						const interval2 = activeShifts[j].recurrenceDetails.interval;
						if (!activeShifts[i].startDate.hasSame(activeShifts[j].startDate, "day") || interval1 !== interval2) {
							continue;
						}
					}
					if (doShiftsConflict(activeShifts[i], activeShifts[j])) {
						if (activeShifts[i].id) conflicts.add(activeShifts[i].id);
						if (activeShifts[j].id) conflicts.add(activeShifts[j].id);
					}
				}
			}
		}
		return conflicts;
	}, [allShifts, recurringGeneralState.existingShiftsToRemove, isOtherRecurrenceList, isMedicalDepartment]);

	// Notify parent about conflicts
	useEffect(() => {
		onHasConflicts?.(conflictingShiftIds.size > 0);
	}, [conflictingShiftIds, onHasConflicts]);

	const handleShiftListItemChange = useCallback(
		({ updatedShift, removed, restored }: { updatedShift: SingleRecurringShift; removed?: boolean; restored?: boolean }) => {
			handleShiftChange(updatedShift, removed ?? false, restored ?? false);
		},
		[handleShiftChange],
	);

	const mostRecentShiftWithStatusNew = useMemo(() => {
		return allShifts
			.filter((shift) => shift.status === SingleRecurringShiftStatus.NEW && shift.lastUIInteraction !== null)
			.sort((a, b) => (b.lastUIInteraction?.valueOf() ?? 0) - (a.lastUIInteraction?.valueOf() ?? 0))
			.at(0);
	}, [allShifts]);

	/**
	 * Returns true if the shift is the most recently added shift with status NEW and was added less than 5 seconds ago
	 */
	const getShouldScrollAndAnimateShift = useCallback(
		(shift: SingleRecurringShift) => {
			const now = DateTime.now().setZone(timezone);
			return mostRecentShiftWithStatusNew?.id === shift.id && now.diff(mostRecentShiftWithStatusNew.lastUIInteraction ?? now, "second").seconds < 5;
		},
		[mostRecentShiftWithStatusNew, timezone],
	);

	return (
		<Box
			data-testid={isOtherRecurrenceList ? "recurring-shift-other-panel" : `recurring-shift-week-panel-${weekAndActiveState.weekNumber}`}
			sx={{
				width: "100%",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
				}}
			>
				<Typography sx={recurringShiftMasterScheduleShiftsListTypographySx}>
					{title} ({allShifts.length}
					{allShifts.length === 1 ? " shift" : " shifts"})
				</Typography>
				<InHouseTooltip title={isNurseScheduledForDeactivation ? t("recurring.shift.staff.scheduled.deactivation") : ""}>
					<span style={{ display: "inline-flex" }}>
						<SystemButton
							sx={(theme) => ({ fontSize: "14px", color: theme.palette.primary.main, p: 0.25 })}
							leadingIcon={
								isNurseScheduledForDeactivation ? (
									<WarningAmberOutlined fontSize={"inherit"} sx={{ color: "warning.main" }} />
								) : (
									<AddIcon fontSize={"inherit"} />
								)
							}
							buttonVariant={"borderless"}
							onClick={handleAddShift}
							disabled={!recurringGeneralState.selectedNurseId || isNurseScheduledForDeactivation}
						>
							Add Shifts
						</SystemButton>
					</span>
				</InHouseTooltip>
			</Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					width: "100%",
				}}
			>
				<AnimatePresence>
					{allShifts.map((shift, index) => {
						return (
							<motion.div
								key={shift.id ?? index}
								layout
								initial={{ opacity: 0, y: -10 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -10 }}
								transition={{ duration: 0.65 }}
							>
								<ShiftListItem
									shift={shift}
									recurringGeneralState={recurringGeneralState}
									onChange={handleShiftListItemChange}
									weekAndActiveState={weekAndActiveState}
									isOtherRecurrenceItem={isOtherRecurrenceList}
									shouldScrollIntoViewOnMount={getShouldScrollAndAnimateShift(shift)}
									shouldAnimateOnMount={getShouldScrollAndAnimateShift(shift)}
									hasConflict={shift.id ? conflictingShiftIds.has(shift.id) : false}
									credentialedLocationIds={credentialedLocationIds}
									allCredentialedLocationIds={allCredentialedLocationIds}
									weekStartDay={weekStartDay}
									selectedNurse={selectedNurse}
								/>
							</motion.div>
						);
					})}
				</AnimatePresence>
			</Box>
		</Box>
	);
};
